import { Controller } from "@hotwired/stimulus"
import { allDevicesStore } from "../../stores/devices/all_devices_store"
import { allBundlesStore } from "../../stores/device_types/all_bundles_store"
import { allDeviceTypesStore } from "../../stores/device_types/all_device_types_store"
import { devicesLoadingStore } from "../../stores/devices/devices_loading_store"
import { devicesBackgroundLoadStore } from "../../stores/devices/devices_background_load_store"
import { useStore } from "stimulus-store"
import { isBundle } from "shared/device_types/device_type_id_helpers";
import { isEmpty } from "lodash";

// Connects to data-controller="table--device-count-table"
export default class extends Controller {
    static stores = [
        allDevicesStore,
        allBundlesStore,
        allDeviceTypesStore,
        // Read-only here: this controller only reacts to loading state via
        // onDevicesLoadingStoreUpdate and never calls a setter, so ordering
        // relative to the other stores does not matter.
        devicesLoadingStore,
        devicesBackgroundLoadStore
    ]

    static targets = [
        "deviceTypeTable",
        "bundleTable"
    ]

    static values = {
        showEmptyMessage: Boolean
    }

    connect() {
        useStore(this)
        this.updateTable()
    }

    /**
     * React to changes in the devices loading store.
     *
     * TRADEOFF (see table-loading-indicators spec, task 8.1): this table renders
     * its own <table> markup and is NOT an `argus--table`, so the shared,
     * centralized overlay API on the `argus--table` controller does not reach it.
     * As the design's recommended option (a), we toggle a small self-contained
     * overlay here that reuses the shared overlay markup (`spinner-border`,
     * `role="status"`, "Loading table data…" label) and SCSS classes
     * (`.argus-table-loading-host`, `.argus-table-loading-overlay`, `.is-visible`)
     * from `_table_loading.scss` for visual consistency.
     *
     * NOTE: because this overlay lives outside `argus--table`, it does NOT inherit
     * the centralized markup/CSS/`Show_Delay` timer/`aria-busy` behavior and must
     * be maintained separately from the shared implementation.
     */
    onDevicesLoadingStoreUpdate(isLoading) {
        // Stay silent during the initial greedy background prefetch (autoLoad);
        // the user hasn't requested data yet. User-initiated loads (area
        // selection) leave this flag false and still show the overlay.
        if (isLoading && this.devicesBackgroundLoadStoreValue === true) {
            return
        }
        this._toggleLoadingOverlay(isLoading)
    }

    /**
     * React to changes in the all devices store
     */
    onAllDevicesStoreUpdate() {
        this.updateTable()
    }

    /**
     * React to changes in device types store (for name lookups)
     */
    onAllDeviceTypesStoreUpdate() {
        this.updateTable()
    }

    /**
     * React to changes in bundles store (for name lookups)
     */
    onAllBundlesStoreUpdate() {
        this.updateTable()
    }

    /**
     * Update the device count table
     */
    updateTable() {
        if (!this.hasDeviceTypeTableTarget) {
            console.warn('Device count table target not found')
            return
        }

        const deviceTypeCounts = this.computeDeviceTypeCounts();
        const bundleCounts = this.computeBundleCounts();
        this.renderTable(deviceTypeCounts, bundleCounts)
    }

    /**
     * Compute device type and bundle counts from all devices
     */
    computeDeviceTypeCounts() {
        const devices = this.allDevicesStoreValue || []
        const deviceTypeCounts = new Map()

        devices.forEach((device) => {

            // Only count devices; not parts
            if (this._isDevice(device)) {
                // Get the current device's device type id
                const deviceTypeId = device.device.device_type.id;

                // Get the current count of this device's device type
                const currentCount = deviceTypeCounts.get(deviceTypeId) || 0

                // Increment the count
                deviceTypeCounts.set(deviceTypeId, currentCount + 1)
            }
        });

        return deviceTypeCounts;
    }

    computeBundleCounts() {
        const devices = this.allDevicesStoreValue || []
        const bundleCounts = new Map()

        // This variable tracks the bundles we've already seen so we don't double count them
        const instantiatedBundles = new Set();

        devices.forEach((device) => {
            // If this isn't a bundle, continue
            if (!isBundle(device)) {
                return;
            }


            // Grab the bundle and bundle_instance_id for this device
            const bundleId = device.bundle.id;
            const bundleInstanceId = device.bundle_instance_id;

            // If we've already seen this bundle instance, keep going
            if (!instantiatedBundles.has(bundleInstanceId)) {
                // Add this bundle instance to the set so we don't double count it
                instantiatedBundles.add(bundleInstanceId);

                // Get the current count of this device's bundle
                const currentCount = bundleCounts.get(bundleId) || 0

                // Increment the count
                bundleCounts.set(bundleId, currentCount + 1)
            }
        });

        return bundleCounts;
    }

    /**
     * Render the device count table
     */
    renderTable(deviceTypeCounts, bundleCounts) {
        const deviceTypeTableRows = []

        // Add device type rows
        deviceTypeCounts.forEach((count, deviceTypeId, _) => {
            // Get the device type's name
            const deviceTypeName = this.getDeviceTypeName(deviceTypeId)

            // Add device type rows
            deviceTypeTableRows.push({
                type: 'device types',
                id: deviceTypeId,
                name: deviceTypeName,
                count: count
            })
        });

        // Sort by name
        deviceTypeTableRows.sort((a, b) => a.name.localeCompare(b.name))

        // Render the table HTML
        this.deviceTypeTableTarget.innerHTML = this.generateTableHTML('device types', deviceTypeTableRows)

        const bundleTableRows = []

        bundleCounts.forEach((count, bundleId, _) => {
            // Get the bundle's name
            const bundleName = this.getBundleName(bundleId);

            // Add bundle rows
            bundleTableRows.push({
                type: 'bundles',
                id: bundleId,
                name: bundleName,
                count: count
            })
        });

        // Render the table HTML
        this.bundleTableTarget.innerHTML = this.generateTableHTML('bundles', bundleTableRows)
    }

    /**
     * Get device type name from store
     */
    getDeviceTypeName(deviceTypeId) {
        const deviceTypes = this.allDeviceTypesStoreValue || []
        const deviceType = deviceTypes.find(dt => dt.id === deviceTypeId)
        return deviceType?.name || `Unknown Device Type (${deviceTypeId})`
    }

    /**
     * Get bundle name from store
     */
    getBundleName(bundleId) {
        const bundles = this.allBundlesStoreValue || []
        const bundle = bundles.find(b => b.id === bundleId)
        return bundle?.name || `Unknown Bundle (${bundleId})`
    }

    /**
     * Generate HTML for the device count table
     */
    generateTableHTML(rowType, rows) {
        // Inform the user there's no device types or bundles
        if (rows.length === 0) {
            return `
                <tr>
                    <td>No ${this.escapeHtml(rowType)} present</td>
                    <td></td>
                </tr>
            `
        } else {
            return rows.map(row => `
            <tr>
                <td>${this.escapeHtml(row.name)}</td>
                <td>${row.count}</td>
            </tr>
        `).join('')
        }

    }

    /**
     * Escape HTML to prevent XSS
     */
    escapeHtml(text) {
        const div = document.createElement('div')
        div.textContent = text
        return div.innerHTML
    }

    /**
     * Manually refresh the table
     */
    refresh() {
        this.updateTable()
    }

    /**
     * Clear the table
     */
    clear() {
        if (this.hasDeviceTypeTableTarget) {
            this.deviceTypeTableTarget.innerHTML = ''
        }
    }

    _isDevice(device) {
        return device?.device?.manufacturer?.id && !isEmpty(device.device.manufacturer.id.trim()) && device?.device?.model?.id && !isEmpty(device.device.model.id.trim());
    }

    /**
     * Toggle the self-contained loading overlay on/off.
     *
     * See onDevicesLoadingStoreUpdate for the tradeoff note: this is a minimal,
     * locally-maintained overlay that mirrors the shared `argus--table` overlay's
     * markup and classes but is intentionally simpler (no `Show_Delay` timer, no
     * `aria-busy` management) because this table is not an `argus--table`.
     */
    _toggleLoadingOverlay(isLoading) {
        const overlay = this._ensureLoadingOverlay()
        if (!overlay) return

        overlay.classList.toggle("is-visible", Boolean(isLoading))
    }

    /**
     * Lazily create the overlay element and turn `this.element` into a
     * positioning host so the absolutely-positioned overlay covers the tables.
     *
     * Reuses the shared SCSS classes from `_table_loading.scss`
     * (`.argus-table-loading-host`, `.argus-table-loading-overlay`, `.is-visible`)
     * and the shared overlay markup (Bootstrap `spinner-border`, `role="status"`,
     * visually-hidden "Loading table data…" label) for visual consistency.
     */
    _ensureLoadingOverlay() {
        if (this._loadingOverlay) return this._loadingOverlay

        // Make the wrapper a positioning context so the overlay can cover it.
        this.element.classList.add("argus-table-loading-host")

        const overlay = document.createElement("div")
        overlay.className = "argus-table-loading-overlay"
        overlay.setAttribute("role", "status")
        overlay.setAttribute("aria-live", "polite")
        overlay.innerHTML = `
            <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading table data…</span>
            </div>
        `

        this.element.appendChild(overlay)
        this._loadingOverlay = overlay
        return overlay
    }
}